import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import styles from "./Overlay.module.css";

/**
 * Overlay primitive: scrim + focus trap + Escape dismissal + focus
 * restoration. Both <Drawer> and <Modal> compose from this.
 *
 * Focus behavior (per §9):
 *   - On open, focus moves to the first focusable element inside.
 *   - Tab is trapped inside; Shift-Tab cycles.
 *   - On close, focus returns to whatever was focused before open.
 *
 * Dismissal:
 *   - Escape key
 *   - Scrim click
 *   - Explicit close button
 *   All call onDismiss WITHOUT committing state — the caller decides
 *   whether to persist any in-flight edit. "Never commits anything on
 *   dismiss" is the drawer rule; modals inherit it.
 */

interface OverlayShellProps {
  open: boolean;
  onDismiss: () => void;
  ariaLabel: string;
  variant: "drawer" | "modal";
  scrimBlur?: boolean;
  children: ReactNode;
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export function OverlayShell({
  open,
  onDismiss,
  ariaLabel,
  variant,
  scrimBlur = false,
  children,
}: OverlayShellProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  // Focus management
  useEffect(() => {
    if (!open) return;

    returnFocusRef.current = document.activeElement as HTMLElement | null;

    // Focus first focusable element inside; fallback to the surface itself.
    const focusable = surfaceRef.current?.querySelectorAll<HTMLElement>(
      FOCUSABLE_SELECTOR,
    );
    if (focusable && focusable.length > 0) {
      focusable[0].focus();
    } else {
      surfaceRef.current?.focus();
    }

    return () => {
      // Restore focus on close
      const target = returnFocusRef.current;
      if (target && typeof target.focus === "function") {
        target.focus();
      }
    };
  }, [open]);

  // Escape + Tab-trap
  useEffect(() => {
    if (!open) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onDismiss();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = surfaceRef.current?.querySelectorAll<HTMLElement>(
        FOCUSABLE_SELECTOR,
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onDismiss]);

  // Body scroll lock while open
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <>
      <div
        className={`${styles.scrim} ${scrimBlur ? styles.scrimBlur : ""}`}
        onClick={onDismiss}
        aria-hidden
      />
      <div
        ref={surfaceRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        className={styles[variant]}
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
